import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Users, 
  TrendingUp, 
  Calendar, 
  Download, 
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Search,
  Shield,
  Target,
  Trophy,
  MapPin,
  Trash2,
  Mail,
  Send,
  AlertTriangle,
  Flag,
  Phone,
  CheckCircle,
  XCircle,
  MoreVertical,
  Eye,
  Newspaper,
  Plus,
  Edit,
  ToggleLeft,
  ToggleRight,
  Clock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminAccounts } from '../components/admin/AdminAccounts';
import { AdminNotifications } from '../components/admin/AdminNotifications';
import { AdminCoursePanel } from '../components/proshop/AdminCoursePanel';
import { useAdminAccess } from '../components/admin/AdminAccessProvider';
import { AdminAccessBoundary } from '../components/admin/AdminAccessGate';
import { SupportInbox } from '../components/support/SupportInbox';
import { SupportThread } from '../components/support/SupportThread';
import '../components/support/support.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboardContent = () => {
  const navigate = useNavigate();
  const { adminKey, adminEmail, isAuthenticated, logout } = useAdminAccess();
  const [stats, setStats] = useState(null);
  const [apiUsage, setApiUsage] = useState(null);
  const [section, setSection] = useState(() => new URLSearchParams(window.location.search).get('section') === 'proshop' ? 'proshop' : 'accounts');
  const [accountVersion, setAccountVersion] = useState(0);
  const [supportVersion, setSupportVersion] = useState(0);
  const [supportThreadId, setSupportThreadId] = useState('');
  const [notificationTarget, setNotificationTarget] = useState(null);
  const [notificationRequester, setNotificationRequester] = useState(null);
  const notifyUser = user => { setNotificationTarget(user); setNotificationRequester(null); setSection('notifications'); };
  const notifyRequester = thread => { setNotificationRequester(thread); setNotificationTarget(null); setSection('notifications'); };
  
  // User management state
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Ticker management state
  const [tickerItems, setTickerItems] = useState([]);
  const [loadingTicker, setLoadingTicker] = useState(false);
  const [showTickerModal, setShowTickerModal] = useState(false);
  const [editingTicker, setEditingTicker] = useState(null);
  const [tickerForm, setTickerForm] = useState({
    text: '',
    priority: 5,
    type: 'custom',
    active: true,
    expires_at: ''
  });
  const [savingTicker, setSavingTicker] = useState(false);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!adminKey) return;
    
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch admin statistics.');
      if ([401,403].includes(error.response?.status)) {
        logout();
        toast.error('Admin sign-in required');
      }
    }
  }, [adminKey, logout]);

  // Fetch API usage
  const fetchApiUsage = useCallback(async () => {
    if (!adminKey) return;
    
    try {
      const response = await axios.get(`${API}/admin/api-usage`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      setApiUsage(response.data);
    } catch (error) {
      console.error('Failed to fetch API usage.');
    }
  }, [adminKey]);

  // Fetch ticker items
  const fetchTickerItems = useCallback(async () => {
    if (!adminKey) return;
    
    setLoadingTicker(true);
    try {
      const response = await axios.get(`${API}/admin/ticker`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      setTickerItems(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch ticker items.');
    } finally {
      setLoadingTicker(false);
    }
  }, [adminKey]);

  // Save ticker item (create or update)
  const saveTickerItem = async () => {
    setSavingTicker(true);
    try {
      if (editingTicker) {
        // Update existing
        await axios.patch(`${API}/admin/ticker/${editingTicker.ticker_id}`, tickerForm, {
          headers: { 'X-Admin-Key': adminKey }
        });
        toast.success('Ticker item updated');
      } else {
        // Create new
        await axios.post(`${API}/admin/ticker`, tickerForm, {
          headers: { 'X-Admin-Key': adminKey }
        });
        toast.success('Ticker item created');
      }
      setShowTickerModal(false);
      setEditingTicker(null);
      setTickerForm({ text: '', priority: 5, type: 'custom', active: true, expires_at: '' });
      fetchTickerItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save ticker item');
    } finally {
      setSavingTicker(false);
    }
  };

  // Delete ticker item
  const deleteTickerItem = async (tickerId) => {
    if (!window.confirm('Delete this ticker item?')) return;
    
    try {
      await axios.delete(`${API}/admin/ticker/${tickerId}`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      toast.success('Ticker item deleted');
      fetchTickerItems();
    } catch (error) {
      toast.error('Failed to delete ticker item');
    }
  };

  // Toggle ticker active status
  const toggleTickerActive = async (item) => {
    try {
      await axios.patch(`${API}/admin/ticker/${item.ticker_id}`, 
        { active: !item.active },
        { headers: { 'X-Admin-Key': adminKey } }
      );
      fetchTickerItems();
    } catch (error) {
      toast.error('Failed to update ticker status');
    }
  };

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setAccountVersion(value => value + 1);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
      fetchUsers();
      fetchApiUsage();
      fetchTickerItems();
    }
  }, [isAuthenticated, fetchStats, fetchUsers, fetchApiUsage, fetchTickerItems]);

  // Export users
  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API}/admin/users/export`, {
        headers: { 'X-Admin-Key': adminKey },
        params: { format }
      });
      
      if (format === 'csv') {
        // Download CSV
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'parseeker_users.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Users exported to CSV');
      } else {
        // Download JSON
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'parseeker_users.json';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Users exported to JSON');
      }
    } catch (error) {
      toast.error('Failed to export users');
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.user_id}`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      toast.success(`User ${selectedUser.name || selectedUser.email} deleted`);
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  // Send email to user
  const handleSendEmail = async () => {
    if (!selectedUser || !emailSubject || !emailMessage) {
      toast.error('Please fill in subject and message');
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/admin/users/${selectedUser.user_id}/email`, {
        subject: emailSubject,
        message: emailMessage
      }, {
        headers: { 'X-Admin-Key': adminKey }
      });
      toast.success(`Email sent to ${selectedUser.email}`);
      setShowEmailModal(false);
      setEmailSubject('');
      setEmailMessage('');
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Open email modal
  const openEmailModal = (user) => {
    setSelectedUser(user);
    setEmailSubject('');
    setEmailMessage('');
    setShowEmailModal(true);
  };

  // Open delete modal
  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // Open user detail modal
  const openUserDetailModal = (user) => {
    setSelectedUser(user);
    setShowUserDetailModal(true);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-stone-100 support-workspace admin-support" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-green-700 text-white p-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6" />
            <div>
              <h1 className="text-xl font-bold">Par Seeker Admin</h1>
              <p className="text-green-200 text-sm">Accounts, notifications &amp; support</p>
              <p className="text-white text-sm break-all" data-testid="admin-dashboard-identity">{adminEmail}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="text-white hover:bg-green-600" onClick={logout} data-testid="admin-dashboard-logout">Sign out</Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { fetchStats(); fetchUsers(); fetchApiUsage(); }}
              className="text-white hover:bg-green-600"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-white hover:bg-green-600"
            >
              Back to App
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 space-y-6">
        <nav className="admin-section-nav" aria-label="Admin sections" data-testid="admin-section-navigation">
          {[['accounts','Accounts'],['proshop','Pro Shop'],['notifications','Notifications'],['support','Support'],['overview','Overview'],['news','Ticker News']].map(([value,title]) => <Button key={value} variant={section === value ? 'default' : 'outline'} aria-pressed={section === value} data-testid={`admin-section-${value}`} onClick={() => setSection(value)}>{title}</Button>)}
        </nav>
        {section === 'accounts' && <AdminAccounts adminKey={adminKey} refreshKey={accountVersion} onDetail={openUserDetailModal} onEmail={openEmailModal} onDelete={openDeleteModal} onExport={handleExport} onNotify={notifyUser} />}
        {section === 'proshop' && <AdminCoursePanel adminKey={adminKey} />}
        {section === 'notifications' && <AdminNotifications key={`${notificationTarget?.user_id || ''}-${notificationRequester?.thread_id || ''}`} adminKey={adminKey} recipient={notificationTarget} requester={notificationRequester} />}
        {section === 'support' && <div className="support-grid"><SupportInbox adminKey={adminKey} selectedId={supportThreadId} onSelect={setSupportThreadId} refreshKey={supportVersion} />{supportThreadId ? <SupportThread key={supportThreadId} threadId={supportThreadId} adminKey={adminKey} onChanged={() => setSupportVersion(v => v + 1)} onNotify={notifyRequester} /> : <p className="support-meta py-12" data-testid="admin-support-select-prompt">Select a request to read and reply.</p>}</div>}
        {section === 'overview' && <>
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Users className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-700">{stats.users.total}</p>
                    <p className="text-xs text-stone-500">Total Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{stats.users.today}</p>
                    <p className="text-xs text-stone-500">Today's Signups</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Calendar className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{stats.users.this_week}</p>
                    <p className="text-xs text-stone-500">This Week</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Target className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-600">{stats.activity.total_scores}</p>
                    <p className="text-xs text-stone-500">Rounds Recorded</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Activity Stats */}
        {stats && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Activity Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xl font-bold">{stats.users.complete_profiles}</p>
                  <p className="text-xs text-stone-500">Complete Profiles</p>
                </div>
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xl font-bold">{stats.activity.total_skins_events}</p>
                  <p className="text-xs text-stone-500">Skins Games</p>
                </div>
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xl font-bold">{stats.activity.total_stroke_play_events}</p>
                  <p className="text-xs text-stone-500">Stroke Play Events</p>
                </div>
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xl font-bold">{stats.activity.total_courses}</p>
                  <p className="text-xs text-stone-500">Courses Added</p>
                </div>
              </div>

              {/* Daily Signups Chart */}
              {stats.users.daily_signups && (
                <div className="mt-6">
                  <p className="text-sm font-medium text-stone-600 mb-3">Signups - Last 7 Days</p>
                  <div className="flex items-end justify-between gap-2 h-24">
                    {stats.users.daily_signups.map((day, idx) => (
                      <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full bg-green-500 rounded-t"
                          style={{ 
                            height: `${Math.max(day.count * 20, 4)}px`,
                            maxHeight: '80px'
                          }}
                        />
                        <span className="text-xs text-stone-500">{day.day}</span>
                        <span className="text-xs font-bold">{day.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* GolfCourseAPI Usage */}
        {apiUsage && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-500" />
                GolfCourseAPI Usage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center mb-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xl font-bold text-blue-600">{apiUsage.today?.cost || 0}</p>
                  <p className="text-xs text-stone-500">Today's Calls</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xl font-bold text-green-600">{apiUsage.today?.remaining || 300}</p>
                  <p className="text-xs text-stone-500">Remaining Today</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-xl font-bold text-amber-600">{apiUsage.period?.total_cost || 0}</p>
                  <p className="text-xs text-stone-500">Last 7 Days</p>
                </div>
                <div className="p-3 bg-stone-100 rounded-lg">
                  <p className="text-xl font-bold">{apiUsage.today?.limit || 300}</p>
                  <p className="text-xs text-stone-500">Daily Limit</p>
                </div>
              </div>
              
              {/* Usage Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-stone-500 mb-1">
                  <span>Today's Usage</span>
                  <span>{Math.round((apiUsage.today?.cost || 0) / (apiUsage.today?.limit || 300) * 100)}%</span>
                </div>
                <div className="w-full bg-stone-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      (apiUsage.today?.cost || 0) > 250 ? 'bg-red-500' : 
                      (apiUsage.today?.cost || 0) > 150 ? 'bg-amber-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min((apiUsage.today?.cost || 0) / (apiUsage.today?.limit || 300) * 100, 100)}%` }}
                  />
                </div>
              </div>
              
              <p className="text-xs text-stone-400 text-center">
                Plan: {apiUsage.plan} • Search = 0.1 call • Full Details = 1.0 call
              </p>
            </CardContent>
          </Card>
        )}

        </>}
        {/* Ticker Management */}
        {section === 'news' &&
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Newspaper className="w-5 h-5 text-amber-500" />
                Ticker News Management
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={fetchTickerItems} disabled={loadingTicker}>
                  <RefreshCw className={`w-4 h-4 mr-1 ${loadingTicker ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button 
                  size="sm" 
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={() => {
                    setEditingTicker(null);
                    setTickerForm({ text: '', priority: 5, type: 'custom', active: true, expires_at: '' });
                    setShowTickerModal(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Ticker Item
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-stone-500 mb-4">
              Custom ticker items appear at the beginning of the "19th Hole Live" news feed. Priority 1 = highest.
            </p>
            
            {loadingTicker ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin" />
              </div>
            ) : tickerItems.length === 0 ? (
              <div className="text-center py-8 text-stone-400">
                <Newspaper className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No custom ticker items yet</p>
                <p className="text-xs">Click "Add Ticker Item" to create announcements</p>
              </div>
            ) : (
              <div className="space-y-2">
                {tickerItems.map((item) => (
                  <div 
                    key={item.ticker_id} 
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      item.active ? 'bg-white border-stone-200' : 'bg-stone-50 border-stone-200 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                        item.priority <= 3 ? 'bg-red-100 text-red-600' :
                        item.priority <= 6 ? 'bg-amber-100 text-amber-600' :
                        'bg-stone-100 text-stone-600'
                      }`}>
                        {item.priority}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate ${!item.active ? 'line-through text-stone-400' : ''}`}>
                          {item.text}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-stone-400">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                            item.type === 'alert' ? 'bg-red-100 text-red-600' :
                            item.type === 'promo' ? 'bg-purple-100 text-purple-600' :
                            item.type === 'news' ? 'bg-blue-100 text-blue-600' :
                            'bg-stone-100 text-stone-600'
                          }`}>
                            {item.type}
                          </span>
                          {item.expires_at && (
                            <span className="flex items-center gap-0.5">
                              <Clock className="w-3 h-3" />
                              Expires: {new Date(item.expires_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => toggleTickerActive(item)}
                        title={item.active ? 'Deactivate' : 'Activate'}
                      >
                        {item.active ? (
                          <ToggleRight className="w-5 h-5 text-green-500" />
                        ) : (
                          <ToggleLeft className="w-5 h-5 text-stone-400" />
                        )}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setEditingTicker(item);
                          setTickerForm({
                            text: item.text,
                            priority: item.priority,
                            type: item.type,
                            active: item.active,
                            expires_at: item.expires_at || ''
                          });
                          setShowTickerModal(true);
                        }}
                      >
                        <Edit className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => deleteTickerItem(item.ticker_id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        }
      </div>

      {/* Delete User Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete User Account
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-stone-600 mb-4">
              Are you sure you want to delete this user? This action cannot be undone.
            </p>
            {selectedUser && (
              <div className="bg-stone-50 rounded-lg p-4">
                <p className="font-medium">{selectedUser.name}</p>
                <p className="text-sm text-stone-500">{selectedUser.email}</p>
                <p className="text-xs text-stone-400 mt-2">
                  This will delete all their posts, scores, messages, and related data.
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteUser}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-500" />
              Send Email to User
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {selectedUser && (
              <div className="bg-stone-50 rounded-lg p-3 flex items-center gap-3">
                {selectedUser.picture ? (
                  <img src={selectedUser.picture} alt="" className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold">
                    {selectedUser.name?.charAt(0) || '?'}
                  </div>
                )}
                <div>
                  <p className="font-medium">{selectedUser.name}</p>
                  <p className="text-sm text-stone-500">{selectedUser.email}</p>
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">Subject</label>
              <Input
                placeholder="Email subject..."
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 block">Message</label>
              <Textarea
                placeholder="Write your message here..."
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows={6}
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailSubject || !emailMessage}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sendingEmail ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Detail Modal */}
      <Dialog open={showUserDetailModal} onOpenChange={setShowUserDetailModal}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              User Details
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="py-4 space-y-4">
              {/* Profile Header */}
              <div className="flex items-center gap-4 pb-4 border-b">
                {selectedUser.picture ? (
                  <img src={selectedUser.picture} alt="" className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-xl">
                    {selectedUser.name?.charAt(0) || '?'}
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold">{selectedUser.name || 'No name'}</h3>
                  {selectedUser.username && <p className="text-stone-500">@{selectedUser.username}</p>}
                  <span className={`text-xs px-2 py-0.5 rounded ${selectedUser.profile_complete ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {selectedUser.profile_complete ? 'Profile Complete' : 'Profile Incomplete'}
                  </span>
                </div>
              </div>

              {/* User Info Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-stone-400 text-xs uppercase">Email</p>
                  <p className="font-medium">{selectedUser.email}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase">User ID</p>
                  <p className="font-mono text-xs">{selectedUser.user_id}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase">Phone</p>
                  <p className="font-medium flex items-center gap-1">
                    {selectedUser.phone_number || 'Not set'}
                    {selectedUser.phone_verified && <CheckCircle className="w-3 h-3 text-green-500" />}
                  </p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase">Location</p>
                  <p className="font-medium">{selectedUser.location || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase">Home Course</p>
                  <p className="font-medium">{selectedUser.home_course || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase">Handicap</p>
                  <p className="font-medium">{selectedUser.handicap ?? 'Not calculated'}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase">Rounds Played</p>
                  <p className="font-medium">{selectedUser.rounds_count || 0}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase">Auth Provider</p>
                  <p className="font-medium capitalize">{selectedUser.auth_provider || 'Email'}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase">Joined</p>
                  <p className="font-medium">{formatDate(selectedUser.created_at)}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase">Last Active</p>
                  <p className="font-medium">{formatDate(selectedUser.last_active) || 'Unknown'}</p>
                </div>
              </div>

              {/* Bio */}
              {selectedUser.bio && (
                <div>
                  <p className="text-stone-400 text-xs uppercase mb-1">Bio</p>
                  <p className="text-sm bg-stone-50 p-3 rounded">{selectedUser.bio}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setShowUserDetailModal(false);
                    openEmailModal(selectedUser);
                  }}
                  className="flex-1"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setShowUserDetailModal(false);
                    openDeleteModal(selectedUser);
                  }}
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete User
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Ticker Item Modal */}
      <Dialog open={showTickerModal} onOpenChange={setShowTickerModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTicker ? 'Edit Ticker Item' : 'Add Ticker Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1">Message Text *</label>
              <Textarea
                value={tickerForm.text}
                onChange={(e) => setTickerForm({ ...tickerForm, text: e.target.value })}
                placeholder="Enter your ticker message (max 100 characters recommended)"
                maxLength={150}
                rows={2}
              />
              <p className="text-xs text-stone-400 mt-1">{tickerForm.text.length}/150 characters</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-stone-700 block mb-1">Priority (1-10)</label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={tickerForm.priority}
                  onChange={(e) => setTickerForm({ ...tickerForm, priority: parseInt(e.target.value) || 5 })}
                />
                <p className="text-xs text-stone-400 mt-1">1 = highest priority</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-stone-700 block mb-1">Type</label>
                <select
                  value={tickerForm.type}
                  onChange={(e) => setTickerForm({ ...tickerForm, type: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm"
                >
                  <option value="custom">Custom</option>
                  <option value="promo">Promo</option>
                  <option value="news">News</option>
                  <option value="alert">Alert</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1">Expires At (optional)</label>
              <Input
                type="datetime-local"
                value={tickerForm.expires_at ? tickerForm.expires_at.slice(0, 16) : ''}
                onChange={(e) => setTickerForm({ ...tickerForm, expires_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
              />
              <p className="text-xs text-stone-400 mt-1">Leave empty for no expiration</p>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="ticker-active"
                checked={tickerForm.active}
                onChange={(e) => setTickerForm({ ...tickerForm, active: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="ticker-active" className="text-sm">Active (visible in ticker)</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTickerModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={saveTickerItem} 
              disabled={!tickerForm.text.trim() || savingTicker}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {savingTicker ? 'Saving...' : (editingTicker ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function AdminDashboard() {
  return <AdminAccessBoundary><AdminDashboardContent /></AdminAccessBoundary>;
}
